// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useEffect, useMemo } from 'react';

import {
  useEvolvePatch,
  type CustomProperty,
  type WorkOrderAggregatedBOMItem,
  useEvolvePaginatedGet,
  type WorkOrderPaginated,
} from '@evolvemep/api-ts';
import { LoadingOverlay } from '@mantine/core';
import { AgGridReact } from 'ag-grid-react';

import { BasePageHeader } from 'components/Mantine/BasePageHeader';
import { ChangeDataViewProvider } from 'components/Mantine/ChangeDataView/useChangeDataView';
import { useCustomProperties } from 'components/Mantine/CustomProperties/useCustomProperties';
import { useGlobalFilters } from 'components/Mantine/GlobalFilters/useGlobalFilters';
import type { CustomStatus, EntityState } from 'components/Mantine/StatusEditor/types';
import { useCustomStatuses } from 'components/Mantine/StatusEditor/useCustomStatuses';
import { detailTable } from 'helpers/ag-grid/DetailTables/detailTable';
import { AgGridEditable } from 'helpers/ag-grid/EditableCells/AgGridEditable';
import { createGridGrouping } from 'helpers/ag-grid/Grouping/createGridGrouping';
import { NoRowsOverlay } from 'helpers/ag-grid/NoRowsOverlay';
import { TABLE_VIEW_TYPE } from 'helpers/ag-grid/TableViewPanel/constants';
import { useServerSideGrid } from 'helpers/ag-grid/useServerSideGrid';
import { hasLengthExactly } from 'helpers/helper-types';
import { isNil } from 'helpers/isNotNil';
import { useWorkOrderStatuses } from 'hooks/typeCacheHooks/useWorkOrderStatuses';
import { useHasAccess } from 'hooks/user/useHasAccess';
import { useCanEditWorkOrder } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/useCanEditWorkOrder';
import type { WorkOrdersPageAgContext } from 'modules/Shop/WorkOrders/WorkOrdersPage/common';
import { NewWorkOrderModal } from 'modules/Shop/WorkOrders/WorkOrdersPage/NewWorkOrderModal';

import { getWorkOrderColDef, getWorkOrderBomColDef, spoolsItemColDef } from './columnDefs';
import { PendingStatusRetirementBanner } from './PendingStatusRetirementBanner';
import { WorkOrdersCalendarView } from './WorkOrdersCalendarView';
import { WorkOrdersKanbanView } from './WorkOrdersKanbanView';

export type WorkOrdersPageProps = {
  states: EntityState<'Work Order'>[];
  statuses: CustomStatus<'Work Order'>[];
  customWorkOrderProperties: CustomProperty[];
};

const WorkOrdersTable = ({ states, statuses, customWorkOrderProperties }: WorkOrdersPageProps) => {
  const { stateColorMap } = useCustomStatuses('Work Order');
  const hasModifyWorkOrderAccess = useHasAccess('workOrders', 'write');
  const { canEditWorkOrder } = useCanEditWorkOrder();

  const {
    fetchPage: fetchWorkOrders,
    setDefaultOpts: setWorkOrdersDefaultOpts,
    abortController: abortWorkOrders,
  } = useEvolvePaginatedGet('shop/workOrder');
  const { fetchPage: fetchBomItems } = useEvolvePaginatedGet('shop/workOrder/:workOrderId/bom');
  const { fetchPage: fetchSpools } = useEvolvePaginatedGet('shop/spool/mappedToWorkOrder');

  const activeStates = useMemo(() => states.filter((s) => s.name !== 'Completed'), [states]);
  const { facilityIds, projectIds, assigneeIds, activeAll, projects } = useGlobalFilters({
    projects: true,
    facilities: true,
    assignees: 'shop/workOrder/assignedUsers',
    activeAll: {
      key: 'Work Orders',
      defaultState: 'active',
      whenActive: activeStates,
      allPossible: states,
    },
  });

  const workOrderColDef = useMemo(
    () =>
      getWorkOrderColDef(
        hasModifyWorkOrderAccess,
        canEditWorkOrder,
        activeAll.state === 'active',
        customWorkOrderProperties,
      ),
    [activeAll.state, canEditWorkOrder, customWorkOrderProperties, hasModifyWorkOrderAccess],
  );

  useEffect(() => {
    // The default `workOrderStatusIds` change when a user toggles the Open/Closed tabs,
    // so we use this effect to change the behavior of fetchPage.
    // Once fetchPage updates, the datasource will be recreated as part of a later useEffect
    // which will cause the entire Grid to reload.
    setWorkOrdersDefaultOpts({
      defaultConfig: {
        params: {
          facilityIds,
          projectIds,
          workOrderStateIds: activeAll.value?.map((s) => s.stateId),
          assigneeIds,
        },
      },
    });
  }, [assigneeIds, facilityIds, activeAll.value, projectIds, setWorkOrdersDefaultOpts]);

  const agGridContext = useMemo<WorkOrdersPageAgContext>(
    () => ({
      statuses,
      entityStates: activeAll.value ?? states,
      stateLabelMap: stateColorMap,
    }),
    [states, statuses, stateColorMap, activeAll.value],
  );

  type Group = {
    section: 'actionable' | 'not actionable';
    label: string | null;
    entireCount?: number;
  };
  const getGroups = useCallback(
    async (): Promise<Group[]> => [
      {
        section: 'actionable',
        label: 'Action Needed',
      },
      {
        section: 'not actionable',
        label: 'Production',
      },
    ],
    [],
  );

  const grouping = useMemo(
    () =>
      createGridGrouping<'shop/workOrder', Group, 'section'>({
        fetchGroups: getGroups,
        rowId: 'section',
        getGroupLabel: (d) => d.label,
        configMapper: (d) => ({
          params: {
            actionable: d.section === 'actionable',
          },
        }),
      }),
    [getGroups],
  );

  const { agGridProps, gridApi } = useServerSideGrid({
    tableName: 'work-orders',
    tableViewType: TABLE_VIEW_TYPE.WORK_ORDERS,
    exportFileName: 'Work Orders',
    colDef: workOrderColDef,
    fetchPage: fetchWorkOrders,
    abortController: abortWorkOrders,
    rowId: 'workOrderId',
    context: agGridContext,
    grouping,
    detailTables: [
      detailTable<
        'shop/workOrder/:workOrderId/bom',
        WorkOrderAggregatedBOMItem,
        WorkOrderPaginated,
        'Bill of Materials'
      >({
        title: 'Bill of Materials' as const,
        icon: 'BillOfMaterials',
        colDef: getWorkOrderBomColDef,
        fetchPage: fetchBomItems,
        exportable: true,
        exportFileName: ({ name, projectName }) => `${projectName}_${name}_BOM`,
        configMapper: ({ workOrderId }: WorkOrderPaginated) => ({
          url: `shop/workOrder/${workOrderId}/bom` as const,
        }),
      }),
      detailTable({
        icon: 'Spools',
        title: 'Spools',
        colDef: spoolsItemColDef,
        fetchPage: fetchSpools,
        exportable: true,
        // retrieving spools linked to the id of the parent work order
        configMapper: ({ workOrderId }: WorkOrderPaginated) => ({
          url: 'shop/spool/mappedToWorkOrder',
          params: { workOrderId },
        }),
        exportFileName: ({ name, projectName }) => `${projectName}_${name}_Spools`,
        hideHeader: true,
      }),
    ],
  });

  const { apiCall: updateWorkOrderApiCall } = useEvolvePatch('shop/workOrder/:workOrderId');

  return (
    <>
      <BasePageHeader
        title="Work Orders"
        topRightComponent={
          <NewWorkOrderModal defaultProject={hasLengthExactly(projects, 1) ? projects[0] : undefined} />
        }
      />

      <PendingStatusRetirementBanner />

      <AgGridEditable
        gridApi={gridApi}
        patchCall={updateWorkOrderApiCall}
        configMapper={(workOrder) => ({ url: `shop/workOrder/${workOrder.workOrderId}` })}
        defaultSortedColId="needByDate"
        requestConverter={(workOrder, updatedData) => ({
          workOrderId: workOrder.workOrderId,
          needByDate: updatedData.needByDate,
          onSiteDate: updatedData.onSiteDate,
          name: updatedData.name,
          description: updatedData.description,
          customProperties: updatedData.customProperties,
          assignees: (updatedData.assignees ?? workOrder.assignees).map((u) => u.userId),
          labels: updatedData.labels?.map((l) => l.labelId) ?? workOrder.labels.map((l) => l.labelId),
        })}
      >
        {(applyWrap) => (
          <AgGridReact<WorkOrderPaginated>
            {...agGridProps}
            onFirstDataRendered={applyWrap}
            noRowsOverlayComponent={() => (
              <NoRowsOverlay
                label={`This facility has no${activeAll.state === 'active' ? 'active ' : ''} work orders.`}
                icon="WorkOrder"
                gridApi={gridApi}
              />
            )}
          />
        )}
      </AgGridEditable>
    </>
  );
};

export const WorkOrdersPage = () => {
  const { customProperties: customWorkOrderProperties } = useCustomProperties('Work Order');
  const { states, statuses, loading } = useWorkOrderStatuses();

  if (loading || isNil(states) || isNil(customWorkOrderProperties)) {
    return <LoadingOverlay visible />;
  }

  return (
    <ChangeDataViewProvider name="Work Orders" viewsToShow={['table', 'kanban', 'calendar']}>
      {(selectedView) => (
        <>
          {selectedView === 'table' && (
            <WorkOrdersTable
              states={states}
              statuses={statuses}
              customWorkOrderProperties={customWorkOrderProperties}
            />
          )}
          {selectedView === 'kanban' && (
            <WorkOrdersKanbanView
              states={states}
              statuses={statuses}
              customWorkOrderProperties={customWorkOrderProperties}
            />
          )}
          {selectedView === 'calendar' && <WorkOrdersCalendarView />}
        </>
      )}
    </ChangeDataViewProvider>
  );
};
